import { Tokens, errorMessage } from '@/constants';
import { environment } from './environment'
import { getSecureItem, removeSecureItem, decodeJwtPayload } from '@/lib/secureStorage';

export default {
    setupInterceptors: (axios, isTokens = false, isFormData = false) => {
        axios.interceptors.request.use((config) => {
            if (isTokens) {
                return config;
            }
            let isToken = getSecureItem(Tokens.ADMIN_TOKEN);
            if (isToken) {
                const payload = decodeJwtPayload(isToken);
                if (payload?.exp && Math.floor(Date.now() / 1000) >= payload.exp) {
                    removeSecureItem(Tokens.ADMIN_TOKEN);
                    removeSecureItem(Tokens.ADMIN);
                    removeSecureItem(Tokens.ADMIN_ROLE);
                    window.location.href = environment.BASE_URL + '/signin';
                    return Promise.reject(new Error('Token expired'));
                }
                config.headers['Authorization'] = `Bearer ${(isToken)}`;
                // W107 (§18): the short-lived proof of presence, when one has been issued.
                const stepUp = getSecureItem(Tokens.STEP_UP_TOKEN);
                if (stepUp) config.headers['X-Step-Up'] = stepUp;
            }
            if (!isToken) {
                // Pages reachable without an account. /policies/shared/:token is
                // opened by external policy readers who will never have a token.
                const publicPaths = ['signin', 'signup', 'reset-password', 'verify-email', 'policies/shared'];
                const isPublicPath = publicPaths.some((path) => window.location.href.includes(path));

                if (!isPublicPath) {
                    window.location.href = environment.BASE_URL + '/signin';
                }
                return config;
            }
            if (isFormData) {
                config.headers['Content-Type'] = 'multipart/form-data';
            }
            return config;
        },
            (error) => {
                return Promise.reject(error);
            }
        );
        axios.interceptors.response.use(
            response => successHandler(response),
            error => errorHandler(error)
        );
        const errorHandler = (error) => {
            const errors = error?.response?.data?.message?.toLowerCase();
            const status = error?.response?.status;
            // Any 401 ends the session. Matching on message text alone missed
            // 401s with an unexpected body (proxy HTML, empty response) and
            // left the user "logged in" while every request failed.
            const isAuthMessage = [
                errorMessage.NOT_AUTHENTICATED,
                errorMessage.TOKEN_NOT_PROVIDED,
                errorMessage.TOKEN_INVALID,
                errorMessage.TOKEN_INVALID_SIGNATURE,
                errorMessage.TOKEN_INVALID_AUTH,
                errorMessage.TOKEN_EXPIRED,
            ].some((message) => message && errors === message.toLowerCase());

            // W51 (ORG-007): the organisation requires a second factor and this
            // account has none — every data path refuses until enrolment, so
            // land on the profile's security card instead of showing errors.
            if (status === 403 && error?.response?.data?.code === 'MFA_ENROLMENT_REQUIRED' && !window.location.pathname.startsWith('/account/profile')) {
                window.location.href = environment.BASE_URL + '/account/profile?mfa=required';
                return Promise.reject(error);
            }
            // W107 (§18): a gated action wants a fresh proof of presence. Ask the person
            // for their password once, then retry the same request with the proof.
            if (status === 428 && error?.response?.data?.code === 'STEP_UP_REQUIRED' && typeof window.__acclimetryStepUp === 'function' && !error?.config?.__stepUpRetried) {
                return window.__acclimetryStepUp(error.response.data).then((stepUpToken) => {
                    if (!stepUpToken) return Promise.reject(error);
                    const retry = { ...error.config, headers: { ...(error.config.headers || {}), 'X-Step-Up': stepUpToken }, __stepUpRetried: true };
                    return axios(retry);
                });
            }
            if (status === 401 || isAuthMessage) {
                removeSecureItem(Tokens.ADMIN_TOKEN);
                removeSecureItem(Tokens.ADMIN);
                removeSecureItem(Tokens.ADMIN_ROLE);
                window.location.href = environment.BASE_URL + '/signin';
                return Promise.reject(error);
            } else if (error?.response?.status === 404) {
                return Promise.reject(error);
            } else if (error?.response?.status === 500) {
                return Promise.reject(error);
            } else {
                return Promise.reject(error);
            }
        };
        const successHandler = (response) => {
            return response;
        };
    }
};
